import { GroupContractItemDto } from 'api/contract';
import {
  Eye,
  Shield,
  XCircle,
  FileSignature,
  File,
  Crown
} from 'lucide-react';
import { contractsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';

interface GenericGroupContractsTableProps {
  forcedFilters?: Record<string, any>;
  title?: string;
  onCreateConfig?: OnCreateConfig;
  selectable?: boolean;
  onViewClick: (groupContract: GroupContractItemDto) => void
  enableFilters?: boolean;
  pageSize?: number;
}

export function GenericGroupContractsTable({
  forcedFilters = {},
  title,
  onCreateConfig,
  onViewClick,
  selectable = true,
  enableFilters = true,
  pageSize = 10,
}: GenericGroupContractsTableProps) {

  // Actions par défaut
  const handleView = (groupContract: GroupContractItemDto) => {
    if (onViewClick)
      onViewClick(groupContract);
  };

  // API avec filtres forcés
  const fetchGroupContracts = async (params: any) => {
    console.log(params);
    const response = await contractsAPI.getGroupContracts({
      getGenericListCommandDto: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || '',
        sorting: params.sorting,
        filters: {
          ...forcedFilters,
          ...params.filters
        }
      }
    });

    return {
      items: response.items,
      totalItems: response.totalCount,
      totalPages: Math.ceil(response.totalCount / params.pageSize),
      currentPage: params.pageNumber
    };
  };

  // Utilitaires
  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      active: { label: 'Actif', className: 'bg-green-100 text-green-700 border-green-300' },
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      expired: { label: 'Expiré', className: 'bg-red-100 text-red-700 border-red-300' },
      suspended: { label: 'Suspendu', className: 'bg-gray-100 text-gray-700 border-gray-300' },
    };

    const config = statusConfig[status.toLowerCase()] || {
      label: status,
      className: 'bg-gray-100 text-gray-700 border-gray-300'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const tableContent = (
    <ServerTable<GroupContractItemDto>
      // Configuration API
      apiConfig={{
        fetchData: fetchGroupContracts,
      }}

      // Colonnes
      columns={[
        {
          key: 'contractIdentifier',
          label: 'Identifiant',
          sortable: true,
          width: '180px',
          render: (value: any) => (
            <span className="font-mono font-semibold text-gray-900">
              {String(value)}
            </span>
          )
        },
        {
          key: 'status',
          label: 'Statut',
          sortable: true,
          width: '130px',
          render: (value: any) => getStatusBadge(String(value))
        },
        {
          key: 'isMaster',
          label: 'Contrat cadre',
          sortable: true,
          align: 'center',
          width: '140px',
          render: (value: any) => (
            <div className="flex items-center justify-center gap-2">
              {value ? (
                <>
                  <Crown className="text-yellow-600" size={18} />
                  <span className="text-sm font-medium text-yellow-700">Oui</span>
                </>
              ) : (
                <>
                  <XCircle className="text-gray-400" size={18} />
                  <span className="text-sm font-medium text-gray-500">Non</span>
                </>
              )}
            </div>
          )
        },
        {
          key: 'scope',
          label: 'Portée',
          sortable: true,
          align: 'center',
          width: '120px',
          render: (value: any) =>
            value === 'Plateforme' ? (
              <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                Plateforme
              </span>
            ) : (
              <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                Cabinet
              </span>
            )
        },
        {
          key: 'owner',
          label: 'Propriétaire',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center gap-2">
              <Shield className="text-primary" size={16} />
              <span className="text-sm text-gray-700">
                {value}
              </span>
            </div>
          )
        },
        {
          key: 'mainDueDate',
          label: 'Échéance',
          sortable: false,
          width: '150px',
          align: 'center',
          render: (value: any) => (
            <span className="text-sm font-medium text-gray-900">
              {value || '-'}
            </span>
          )
        },
        {
          key: 'activeContractsCount',
          label: 'Contrats actifs',
          sortable: true,
          align: 'center',
          width: '130px',
          render: (value: any) => (
            <div className="flex items-center justify-center gap-2">
              <FileSignature className="text-primary" size={16} />
              <span className="font-semibold text-gray-900">
                {String(value)}
              </span>
            </div>
          )
        },
        {
          key: 'quotesCount',
          label: 'Devis créés',
          sortable: true,
          align: 'center',
          width: '130px',
          render: (value: any) => (
            <div className="flex items-center justify-center gap-2">
              <File className="text-primary" size={16} />
              <span className="font-semibold text-gray-900">
                {String(value)}
              </span>
            </div>
          )
        },
        {
          key: 'creationTime',
          label: 'Date de création',
          sortable: true,
          width: '150px',
          render: (value: any) => (
            <span className="text-sm text-gray-600">
              {formatDate(String(value))}
            </span>
          )
        },
        {
          key: 'lastModificationTime',
          label: 'Dernière mise à jour',
          sortable: true,
          width: '150px',
          render: (value: any) => (
            <span className="text-sm text-gray-600">
              {value ? formatDate(String(value)) : '-'}
            </span>
          )
        }
      ]}

      // En-tête
      title={title ? {
        label: title,
        iconKey: 'folder-open'
      } : undefined}

      // Actions
      actions={{
        create: onCreateConfig,
        row: [
          {
            icon: <Eye size={16} />,
            onClick: handleView,
            variant: 'slate'
          },
        ],
      }}

      // Configuration
      selectable={selectable}
      pageSize={pageSize}
      pageSizeOptions={[5, 10, 20, 50]}
      showPaginationInfo

      // Recherche et filtres
      enableSearch
      enableFilters={enableFilters}
      searchPlaceholder="Rechercher un groupe de contrats (identifiant, propriétaire, statut...)"

      // Skeleton
      skeletonRowCount={8}
    />
  );

  return tableContent;
}
